import { useEffect, useState } from "react";
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  doc,
} from "firebase/firestore";
import { db } from "@/lib/firebase";
import type { Channel, ChannelMember } from "@/lib/types";

export interface ChannelWithMembership extends Channel {
  id: string;
  isMember: boolean;
  isPending: boolean;
  hasUnread: boolean;
}

export function useChannels(clubId: string | undefined, userId: string | undefined) {
  const [channelDocs, setChannelDocs] = useState<(Channel & { id: string })[]>([]);
  const [memberStatuses, setMemberStatuses] = useState<Record<string, string | null>>({});
  const [lastReadByChannel, setLastReadByChannel] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);

  // Listen to channel list
  useEffect(() => {
    if (!clubId) {
      setChannelDocs([]);
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, "clubs", clubId, "channels"),
      orderBy("createdAt", "asc")
    );

    const unsub = onSnapshot(q, (snap) => {
      const items = snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      })) as (Channel & { id: string })[];
      setChannelDocs(items);
      setLoading(false);
    });

    return unsub;
  }, [clubId]);

  // Listen to membership status for each channel (real-time)
  useEffect(() => {
    if (!clubId || !userId || channelDocs.length === 0) return;

    const unsubs: (() => void)[] = [];

    for (const ch of channelDocs) {
      const memberRef = doc(db, "clubs", clubId, "channels", ch.id, "members", userId);
      const unsub = onSnapshot(memberRef, (snap) => {
        setMemberStatuses((prev) => ({
          ...prev,
          [ch.id]: snap.exists() ? (snap.data() as ChannelMember).status : null,
        }));
        const lr = (snap.data() as any)?.lastReadAt;
        setLastReadByChannel((prev) => ({
          ...prev,
          [ch.id]: lr?.toMillis?.() || 0,
        }));
      });
      unsubs.push(unsub);
    }

    return () => unsubs.forEach((u) => u());
  }, [clubId, userId, channelDocs.length]);

  // Combine channels with membership status + unread state
  const channels: ChannelWithMembership[] = channelDocs
    .map((ch) => {
      const lastMsgMs = (ch as any).lastMessageAt?.toMillis?.() || 0;
      const lastReadMs = lastReadByChannel[ch.id] || 0;
      const isMember = memberStatuses[ch.id] === "active";
      return {
        ...ch,
        isMember,
        isPending: memberStatuses[ch.id] === "pending",
        hasUnread: isMember && lastMsgMs > lastReadMs,
      };
    })
    .filter((ch) => {
      if (ch.visibility !== "private") return true;
      return ch.isMember || ch.isPending;
    });

  const unreadChannelCount = channels.filter((c) => c.hasUnread).length;

  return { channels, loading, unreadChannelCount };
}
